/* eslint-disable jsx-a11y/anchor-has-content */
import * as React from 'react';

import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { buttonVariants } from '@/components/Button';
import { useMediaQuery } from '@/hooks';
import { cn, range } from '@/utils';

const BOUNDARY_COUNT = 1;

// Reference https://github.com/mui/material-ui/blob/next/packages/mui-material/src/usePagination/usePagination.js
function useItems(page, totalPages, SIBLING_COUNT) {
	return React.useMemo(() => {
		const startPages = range(1, Math.min(BOUNDARY_COUNT, totalPages));
		const endPages = range(
			Math.max(totalPages - BOUNDARY_COUNT + 1, BOUNDARY_COUNT + 1),
			totalPages
		);

		const siblingsStart = Math.max(
			Math.min(
				// Natural start
				page - SIBLING_COUNT,
				// Lower boundary when page is high
				totalPages - BOUNDARY_COUNT - SIBLING_COUNT * 2 - 1
			),
			// Greater than startPages
			BOUNDARY_COUNT + 2
		);

		const siblingsEnd = Math.min(
			Math.max(
				// Natural end
				page + SIBLING_COUNT,
				// Upper boundary when page is low
				BOUNDARY_COUNT + SIBLING_COUNT * 2 + 2
			),
			// Less than endPages
			endPages.length > 0 ? endPages[0] - 2 : totalPages - 1
		);

		// Basic list of items to render
		// for example itemList = [1, 'ellipsis', 4, 5, 6, 'ellipsis', 10]
		return [
			...startPages,

			// Start ellipsis
			// eslint-disable-next-line no-nested-ternary
			...(siblingsStart > BOUNDARY_COUNT + 2
				? ['ellipsis']
				: BOUNDARY_COUNT + 1 < totalPages - BOUNDARY_COUNT
					? [BOUNDARY_COUNT + 1]
					: []),

			// Sibling pages
			...range(siblingsStart, siblingsEnd),

			// End ellipsis
			// eslint-disable-next-line no-nested-ternary
			...(siblingsEnd < totalPages - BOUNDARY_COUNT - 1
				? ['ellipsis']
				: totalPages - BOUNDARY_COUNT > BOUNDARY_COUNT
					? [totalPages - BOUNDARY_COUNT]
					: []),

			...endPages
		];
	}, [page, totalPages]);
}

const PaginationContent = React.forwardRef(({ className, ...props }, ref) => (
	<ul
		ref={ref}
		className={cn('flex flex-row items-center gap-1', className)}
		{...props}
	/>
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef(({ className, ...props }, ref) => (
	<li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

function PaginationButton({ className, isActive, ...props }) {
	return (
		<button
			type="button"
			aria-current={isActive ? 'page' : undefined}
			className={cn(
				buttonVariants({
					variant: 'outline',
					size: 'iconMd'
				}),
				'rounded-md text-sm font-normal text-black',
				isActive && 'border-red bg-white text-red',
				className
			)}
			{...props}
		/>
	);
}
PaginationButton.displayName = 'PaginationLink';

function PaginationPrevious({ className, ...props }) {
	const { t } = useTranslation();

	return (
		<PaginationButton aria-label={t('pagination.buttonPrevious')} {...props}>
			<ChevronLeft aria-hidden className="h-4 w-4" />
		</PaginationButton>
	);
}
PaginationPrevious.displayName = 'PaginationPrevious';

function PaginationNext({ className, ...props }) {
	const { t } = useTranslation();

	return (
		<PaginationButton aria-label={t('pagination.buttonNext')} {...props}>
			<ChevronRight aria-hidden className="h-4 w-4" />
		</PaginationButton>
	);
}
PaginationNext.displayName = 'PaginationNext';

function PaginationEllipsis({ className, ...props }) {
	const { t } = useTranslation();

	return (
		<span
			className={cn('flex h-9 w-9 items-center justify-center', className)}
			{...props}
		>
			<MoreHorizontal aria-hidden className="mt-3 h-4 w-4" />
			<span className="sr-only">{t('pagination.morePages')}</span>
		</span>
	);
}
PaginationEllipsis.displayName = 'PaginationEllipsis';

function Pagination({ className, totalPages, page, onChange, ...props }) {
	const mdDown = useMediaQuery('mdDown');
	const { t } = useTranslation();

	const SIBLING_COUNT = mdDown ? 0 : 1;
	const items = useItems(page, totalPages, SIBLING_COUNT);

	return (
		<nav
			role="navigation"
			aria-label={t('navLabel')}
			className={cn('mx-auto flex w-full justify-center gap-1', className)}
			{...props}
		>
			<PaginationContent>
				<PaginationItem>
					<PaginationPrevious
						disabled={page === 1}
						onClick={() => onChange(page - 1)}
					/>
				</PaginationItem>
				{items.map((item, index) => (
					<PaginationItem key={index}>
						{item === 'ellipsis' ? (
							<PaginationEllipsis />
						) : (
							<PaginationButton
								isActive={page === item}
								onClick={() => onChange(item)}
							>
								{item}
							</PaginationButton>
						)}
					</PaginationItem>
				))}
				<PaginationItem>
					<PaginationNext
						disabled={page === totalPages}
						onClick={() => onChange(page + 1)}
					/>
				</PaginationItem>
			</PaginationContent>
		</nav>
	);
}
Pagination.displayName = 'Pagination';

export default Pagination;
